import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { Calendar, Download, Printer, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface ReportData {
  totalSales: number;
  totalExpenses: number;
  netProfit: number;
  salesCount: number;
  expensesCount: number;
  topSellingProducts: Array<{
    product_name: string;
    quantity: number;
    revenue: number;
  }>;
  expensesByCategory: Array<{
    category_name: string;
    total_amount: number;
    count: number;
  }>;
}

export default function PharmacyReports() {
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState<ReportData>({
    totalSales: 0,
    totalExpenses: 0,
    netProfit: 0,
    salesCount: 0,
    expensesCount: 0,
    topSellingProducts: [],
    expensesByCategory: []
  });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadReportData();
  }, [dateRange]);

  const loadReportData = async () => {
    try {
      setLoading(true);

      const { data: salesData, error: salesError } = await supabase
        .from('sales_invoices')
        .select('total, discount_amount')
        .gte('created_at', `${dateRange.startDate}T00:00:00`)
        .lte('created_at', `${dateRange.endDate}T23:59:59`);

      if (salesError) throw salesError;

      const { data: expensesData, error: expensesError } = await supabase
        .from('pharmacy_expenses')
        .select(`
          amount,
          category:pharmacy_expense_categories(name)
        `)
        .gte('expense_date', dateRange.startDate)
        .lte('expense_date', dateRange.endDate);

      if (expensesError) throw expensesError;

      const { data: topProducts, error: topProductsError } = await supabase
        .from('sales_items')
        .select(`
          product_id,
          quantity,
          total,
          product:products(name)
        `)
        .gte('created_at', `${dateRange.startDate}T00:00:00`)
        .lte('created_at', `${dateRange.endDate}T23:59:59`);

      if (topProductsError) throw topProductsError;

      const totalSales = salesData?.reduce((sum, sale) => sum + (sale.total || 0), 0) || 0;
      const totalExpenses = expensesData?.reduce((sum, exp) => sum + (exp.amount || 0), 0) || 0;
      const netProfit = totalSales - totalExpenses;

      const productMap = new Map<string, { name: string; quantity: number; revenue: number }>();
      topProducts?.forEach(item => {
        const productName = (item as any).product?.name || 'Unknown';
        const existing = productMap.get(productName);
        if (existing) {
          existing.quantity += item.quantity;
          existing.revenue += item.total;
        } else {
          productMap.set(productName, {
            name: productName,
            quantity: item.quantity,
            revenue: item.total
          });
        }
      });

      const topSellingProducts = Array.from(productMap.values())
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)
        .map(p => ({
          product_name: p.name,
          quantity: p.quantity,
          revenue: p.revenue
        }));

      const categoryMap = new Map<string, { total: number; count: number }>();
      expensesData?.forEach(expense => {
        const categoryName = (expense as any).category?.name || 'Uncategorized';
        const existing = categoryMap.get(categoryName);
        if (existing) {
          existing.total += expense.amount;
          existing.count += 1;
        } else {
          categoryMap.set(categoryName, { total: expense.amount, count: 1 });
        }
      });

      const expensesByCategory = Array.from(categoryMap.entries())
        .map(([name, data]) => ({
          category_name: name,
          total_amount: data.total,
          count: data.count
        }))
        .sort((a, b) => b.total_amount - a.total_amount);

      setReportData({
        totalSales,
        totalExpenses,
        netProfit,
        salesCount: salesData?.length || 0,
        expensesCount: expensesData?.length || 0,
        topSellingProducts,
        expensesByCategory
      });
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    let csv = 'Pharmacy Report\n\n';
    csv += `Date Range: ${dateRange.startDate} to ${dateRange.endDate}\n\n`;
    csv += 'Summary\n';
    csv += `Total Sales,${reportData.totalSales}\n`;
    csv += `Total Expenses,${reportData.totalExpenses}\n`;
    csv += `Net Profit,${reportData.netProfit}\n\n`;
    csv += 'Top Selling Products\n';
    csv += 'Product Name,Quantity,Revenue\n';
    reportData.topSellingProducts.forEach(p => {
      csv += `${p.product_name},${p.quantity},${p.revenue}\n`;
    });
    csv += '\nExpenses by Category\n';
    csv += 'Category,Amount,Count\n';
    reportData.expensesByCategory.forEach(c => {
      csv += `${c.category_name},${c.total_amount},${c.count}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pharmacy-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-primary-900">Pharmacy Report</h1>
          <p className="text-sm text-neutral-600">Comprehensive pharmacy performance report</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrint}
            className="inline-flex items-center px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
          >
            <Printer className="h-5 w-5 mr-2" />
            Print
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Download className="h-5 w-5 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <div className="flex items-center gap-4">
          <Calendar className="h-5 w-5 text-neutral-400" />
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-100 uppercase tracking-wide">Total Sales</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(reportData.totalSales)}</p>
              <p className="text-xs text-green-100 mt-1">{reportData.salesCount} transactions</p>
            </div>
            <div className="bg-white/20 p-3 rounded-lg">
              <TrendingUp className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-100 uppercase tracking-wide">Total Expenses</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(reportData.totalExpenses)}</p>
              <p className="text-xs text-red-100 mt-1">{reportData.expensesCount} expenses</p>
            </div>
            <div className="bg-white/20 p-3 rounded-lg">
              <TrendingDown className="h-8 w-8" />
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-br ${reportData.netProfit >= 0 ? 'from-primary-500 to-primary-600' : 'from-amber-500 to-amber-600'} rounded-lg shadow-lg p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/90 uppercase tracking-wide">Net Profit</p>
              <p className="text-3xl font-bold mt-2">{formatCurrency(reportData.netProfit)}</p>
              <p className="text-xs text-white/90 mt-1">
                {reportData.netProfit >= 0 ? 'Profitable' : 'Loss'}
              </p>
            </div>
            <div className="bg-white/20 p-3 rounded-lg">
              <DollarSign className="h-8 w-8" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900">Top Selling Products</h3>
          </div>
          <div className="p-6">
            {reportData.topSellingProducts.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">No sales data available</p>
            ) : (
              <div className="space-y-3">
                {reportData.topSellingProducts.map((product, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-neutral-900">{product.product_name}</p>
                        <p className="text-xs text-neutral-500">{product.quantity} units sold</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrency(product.revenue)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900">Expenses by Category</h3>
          </div>
          <div className="p-6">
            {reportData.expensesByCategory.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-8">No expense data available</p>
            ) : (
              <div className="space-y-3">
                {reportData.expensesByCategory.map((category, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div>
                      <p className="font-medium text-neutral-900">{category.category_name}</p>
                      <p className="text-xs text-neutral-500">{category.count} expenses</p>
                    </div>
                    <span className="text-sm font-semibold text-red-600">
                      {formatCurrency(category.total_amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
